'use client';
// Decomposes the live portfolio's YTD return into the part contributed by
// names sourced from the visualizer (the model's recommendations Steve
// actually bought) vs names that were already in the book before the
// visualizer surfaced them. Also surfaces the cash share.
//
// Data sources:
//   /api/portfolio        — symbols, weights, cashWeight, visualizerSourced[]
//   /api/holdings-pnl     — per-symbol ytdPct (decimal)
//
// "Visualizer-sourced" is hand-tagged in data/portfolio.json under the
// `visualizerSourced` field. Per Steve 2026-05-07: MU, VST, TLN are the
// names he bought because of the model; the rest he held before. Edit the
// JSON when a new visualizer pick lands in the book.

import { useMemo } from 'react';
import { useResource } from '@/lib/use-resource';

interface PortfolioResp {
  symbols: string[];
  weights: Record<string, number>;
  cashWeight: number;
  visualizerSourced?: string[];
}
interface PnlEntry { ytdPct: number | null }
interface PnlResp { bySymbol: Record<string, PnlEntry> }
interface BasketSlot { current: { symbol: string } }
// /api/alpha-basket envelope: { basket: { slots: [...] }, displaySlots, aggregates, events }.
// Reading directly off `data.slots` (without the basket wrapper) would always
// produce an empty set and silently kill the alpha-zone-overlap row.
// displaySlots is the BUY+ filtered roster (composite ≥ 1.5) — use it for any
// "is this name in the alpha zone" question so HOLD-decayed slots don't count.
interface BasketResp { basket?: { slots?: BasketSlot[] }; displaySlots?: BasketSlot[] }

interface Bucket {
  label: string;
  contributionPct: number;
  weightPct: number;
  symbols: string[];
  color: string;
}

const fmtPp = (v: number, digits = 2) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}pp`;
const fmtPct = (v: number, digits = 2) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}%`;

export function AlphaZoneAttribution() {
  const portfolio = useResource<PortfolioResp>('/api/portfolio');
  const pnl = useResource<PnlResp>('/api/holdings-pnl');
  // Steve directive 2026-05-09: also surface the YTD contribution from names
  // that are in the live alpha basket (intersection of portfolio + basket).
  // This is a SEPARATE bucket from "visualizer-sourced" — visualizer-sourced
  // is hand-tagged in portfolio.json (names Steve bought because of the model);
  // alpha-zone is the live model-current basket membership.
  const basket = useResource<BasketResp>('/api/alpha-basket');

  const result = useMemo(() => {
    if (!portfolio.data || !pnl.data) return null;
    const sourced = new Set(portfolio.data.visualizerSourced ?? []);
    const slots = basket.data?.displaySlots ?? basket.data?.basket?.slots ?? [];
    const basketSyms = new Set(slots.map((s) => s.current.symbol));
    const weights = portfolio.data.weights;
    const bySym = pnl.data.bySymbol;
    let visContrib = 0, visWeight = 0;
    let preContrib = 0, preWeight = 0;
    let azContrib = 0, azWeight = 0;
    const visSyms: string[] = [];
    const preSyms: string[] = [];
    const azSyms: string[] = [];
    for (const sym of portfolio.data.symbols) {
      const w = weights[sym];
      const ytd = bySym[sym]?.ytdPct;
      if (w == null || ytd == null) continue;
      const contrib = w * ytd;
      if (sourced.has(sym)) {
        visContrib += contrib; visWeight += w; visSyms.push(sym);
      } else {
        preContrib += contrib; preWeight += w; preSyms.push(sym);
      }
      if (basketSyms.has(sym)) {
        azContrib += contrib; azWeight += w; azSyms.push(sym);
      }
    }
    const equityYtd = visContrib + preContrib;
    return {
      visualizer: { label: 'Visualizer ideas', contributionPct: visContrib * 100, weightPct: visWeight * 100, symbols: visSyms, color: '#c9a84c' } as Bucket,
      preexisting: { label: 'Pre-existing book', contributionPct: preContrib * 100, weightPct: preWeight * 100, symbols: preSyms, color: '#475569' } as Bucket,
      alphaZone: { label: 'Alpha Zone overlap', contributionPct: azContrib * 100, weightPct: azWeight * 100, symbols: azSyms, color: '#10b981' } as Bucket,
      cashWeightPct: portfolio.data.cashWeight * 100,
      portfolioYtdPct: equityYtd * 100,
    };
  }, [portfolio.data, pnl.data, basket.data]);

  if (portfolio.loading || pnl.loading) {
    return (
      <div className="border border-[#1e293b] rounded-lg bg-[#0d1117] p-6 text-center text-[#64748b] text-sm">
        loading attribution…
      </div>
    );
  }
  if (!result) return null;

  const { visualizer, preexisting, alphaZone, cashWeightPct, portfolioYtdPct } = result;
  const headlineColor = portfolioYtdPct >= 0 ? 'text-emerald-300' : 'text-red-400';

  const totalAbsContrib = Math.max(
    Math.abs(visualizer.contributionPct) + Math.abs(preexisting.contributionPct),
    0.01,
  );
  const visWidth = Math.abs(visualizer.contributionPct) / totalAbsContrib * 100;
  const preWidth = Math.abs(preexisting.contributionPct) / totalAbsContrib * 100;

  const visList = visualizer.symbols.length > 0 ? visualizer.symbols.join(', ') : 'none currently tagged';
  // Alpha-zone summary line: how much of the portfolio's YTD came from names
  // that are CURRENTLY in the live top-20 alpha basket (intersection of book
  // + basket). Different from visualizer-sourced — basket membership is
  // model-state-now, not a hand-tagged historical attribution.
  const azPctOfBook = portfolioYtdPct !== 0 ? (alphaZone.contributionPct / portfolioYtdPct) * 100 : 0;

  return (
    <div className="border border-[#1e293b] rounded-lg bg-[#0d1117] px-3 py-2">
      <div className="flex items-baseline justify-between mb-1.5 flex-wrap gap-x-3">
        <h3 className="text-[var(--gold,#c9a84c)] text-[11px] font-semibold uppercase tracking-wider">
          YTD Attribution
        </h3>
        <span className={`text-[13px] font-bold tabular-nums ${headlineColor}`}>
          {fmtPct(portfolioYtdPct)}
        </span>
      </div>

      {/* Stacked bar — equity book only */}
      <div className="flex h-4 rounded overflow-hidden bg-[#1e293b] mb-1.5">
        <div
          className="flex items-center justify-center text-[9px] font-semibold text-[#121722]"
          style={{ width: `${visWidth}%`, backgroundColor: visualizer.color, minWidth: visWidth > 0 ? '1.5rem' : 0 }}
          title={`${visualizer.symbols.length} names · ${visualizer.weightPct.toFixed(1)}% weight · ${visList}`}
        >
          {visWidth > 12 ? fmtPp(visualizer.contributionPct, 1) : ''}
        </div>
        <div
          className="flex items-center justify-center text-[9px] font-semibold text-white"
          style={{ width: `${preWidth}%`, backgroundColor: preexisting.color, minWidth: preWidth > 0 ? '1.5rem' : 0 }}
          title={`${preexisting.symbols.length} names · ${preexisting.weightPct.toFixed(1)}% weight`}
        >
          {preWidth > 12 ? fmtPp(preexisting.contributionPct, 1) : ''}
        </div>
      </div>

      {/* Legend — single tight row, all same font */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px]">
        <BucketRow bucket={visualizer} />
        <BucketRow bucket={preexisting} />
        {alphaZone.symbols.length > 0 && (
          <span className="inline-flex items-baseline gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 self-center" />
            <span className="text-[#94a3b8] uppercase tracking-wide text-[9px]">Alpha Zone overlap</span>
            <span className="text-emerald-300 font-bold tabular-nums">{fmtPp(alphaZone.contributionPct, 2)}</span>
            <span className="text-[#64748b]">{alphaZone.symbols.length} names · {alphaZone.weightPct.toFixed(1)}% · {portfolioYtdPct !== 0 ? `${azPctOfBook.toFixed(0)}% of YTD` : ''}</span>
          </span>
        )}
        <span className="inline-flex items-baseline gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9] self-center" />
          <span className="text-[#94a3b8] uppercase tracking-wide text-[9px]">Cash</span>
          <span className="text-white font-bold tabular-nums">{cashWeightPct.toFixed(1)}%</span>
        </span>
      </div>
    </div>
  );
}

function BucketRow({ bucket }: { bucket: Bucket }) {
  const contribColor = bucket.contributionPct >= 0 ? 'text-emerald-300' : 'text-red-400';
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full self-center" style={{ backgroundColor: bucket.color }} />
      <span className="text-[#94a3b8] uppercase tracking-wide text-[9px]">{bucket.label}</span>
      <span className={`font-bold tabular-nums ${contribColor}`}>{fmtPp(bucket.contributionPct)}</span>
      <span className="text-[#64748b]">{bucket.symbols.length} names · {bucket.weightPct.toFixed(1)}%</span>
    </span>
  );
}
